import React, { useState, useEffect, useMemo } from "react";
import NormalTable from "../../../../utility/table/NormalTable.jsx";
import { useTranslation } from "react-i18next";
import CommonComponent from "../../CommonComponent";
import useAdsAnalysis from "./useAdsAnalysis";
import usePersistentDateRange from "../../Common/usePersistentDateRange";
import CategoryPerformanceDashboard from "./ChartLayout/CategoryPerformanceDashboard";
import NoDataFound from "../NoDataFound/NoDataFound";
import Spinner from "../NoDataFound/Spinner";
import DashboardReady from "../DashboardReady/DashboardReady";
import AdsSalesCard from "./AdsSalesCard";
import categorySelectionDynamic from "../../Common/categorySelectionDynamic";
import Tabs from "../TableTab/TableTab";
import ViewTypeSelect from "../ViewTypeSelect";
import { useOption } from "../../../../context/OptionContext.js";
import { useStatus } from "../../../../context/StatusContext.js";
import KpiAccordion from "../DashboardHome/KpiCard.jsx";
import PermissionControlledCard from "../../../../common/PermissionControlledCard.jsx";
import { usePermissions } from "../../../../context/PermissionContext.js";
import SalesCard from "../DashboardHome/SalesCard.jsx";
import AllowedCard from "../../../../common/AllowedCard.jsx";
import { addColumn } from "../../Common/addColumn.js";

const AdsAnalysis = () => {
  const {
    combinedPermission,
    canShowTable,
    combinedTablePermission,
    filteredTabs,
    selectedTabPermission,
    adsAnalyseLoaded,
    loadingState,
    categoryPerformanceState,
    adsAnalyseSequence,
    adsAnalyseState,
    getCategoryPerformanceDashbaord,
    getAdsAnalyse,
    selectedTab,
    setSelectedTab,
    tabOptions,
    accessDenied
  } = useAdsAnalysis();

  
  const { t } = useTranslation();
  const permission = usePermissions();
  const { selectedRange, handleDateChange, formattedStart, formattedEnd } =
    usePersistentDateRange();
  const adsAnalyseStateEmpty =
    !adsAnalyseState || Object.keys(adsAnalyseState).length === 0;
  const { selectedOption } = useOption();
  const { statusValues } = useStatus();
  const [viewType, setViewType] = useState(() => {
    if (
      permission?.data?.ads_analysis?.view_format.category_wise.display &&
      permission?.data?.ads_analysis?.view_format.category_wise.allowed
    ) {
      // localStorage.setItem("viewType", "category");
      return localStorage.getItem("viewType");
    }
    else if (
      permission?.data?.ads_analysis?.view_format.product_wise.display &&
      permission?.data?.ads_analysis?.view_format.product_wise.allowed
    ) {
      // localStorage.setItem("viewType", "product");
      return localStorage.getItem("viewType");
    }
    return localStorage.getItem("viewType");
  });

  useEffect(() => {
    const viewFormat = permission?.data?.ads_analysis?.view_format;
    if (!viewFormat) return;

    const savedView = localStorage.getItem("viewType");
    const isProductAllowed = viewFormat?.product_wise?.allowed;
    const isCategoryAllowed = viewFormat?.category_wise?.allowed;

    let newView = savedView;

    //  Switch only if current view is not allowed
    if (savedView === "category" && !isCategoryAllowed && isProductAllowed) {
      newView = "product";
    }
    else if (savedView === "product" && !isProductAllowed && isCategoryAllowed) {
      newView = "category";
    }

    if (newView !== savedView) {
      setViewType(newView);
      localStorage.setItem("viewType", newView);
    }
  }, [
    permission?.data?.ads_analysis?.view_format?.product_wise?.allowed,
    permission?.data?.ads_analysis?.view_format?.category_wise?.allowed
  ]);

  const handleColumnChange = async(selectedCols, allColumns) => {
    const formattedColumns = allColumns
      .filter(col => col !== "id")
      .map(col => ({
        key: col,
        visible: selectedCols.includes(col)
      }));

    await addColumn({
      data: {
        section_name: "ads",
        columns: formattedColumns
      }
    });
    getAdsAnalyse({
    formattedStart,
    formattedEnd,
    selectedIds,
    selectedTab
  });
  };
    
  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
    localStorage.setItem("viewType", e.target.value);
  };

  const [selectedIds, setSelectedIds] = useState({
    productId: [],
    categoryId: [],
    parentProductId: [],
  });

  const sectionPermissionMap = {
    salesAndRevenue: "sales_metrics",
    orderAndUnit: "performance_metrics",
    returnAndRejection: "returns_and_quality_metrics",
    customerAndEngagement: "efficiency_metrics",
  };

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const downloadBtnPermission = permission?.data?.ads_analysis?.table_section?.download_option?.display && permission?.data?.ads_analysis?.table_section?.download_option?.allowed
    const handlerFns = useMemo(() => [
      getAdsAnalyse,
    ], [getAdsAnalyse]);
  
  const {
    categoryId,
    categoryName,
    handleCategoryClick,
    onBreadcrumbClick,
    handleParentProductClick,
  } = categorySelectionDynamic(
    handlerFns,
    formattedStart,
    formattedEnd,
    selectedTab,
    viewType
  );
  
  useEffect(() => {
      getAdsAnalyse({ formattedEnd, formattedStart, selectedTab });
      getCategoryPerformanceDashbaord({ formattedEnd, formattedStart, selectedTab, viewType });
  }, [
    formattedStart,
    formattedEnd,
    selectedTab,
    viewType,
    selectedOption?.id,
  ]);

  useEffect(() => {
    setIsButtonDisabled(false); // Re-enable when user changes selection
  }, [selectedIds]);

  const handleSelectionChange = (ids) => {
    const hasNoSelection =
      ids.productId.length === 0 && ids.categoryId.length === 0;

    if (
      selectedIds.productId.length + selectedIds.categoryId.length > 0 &&
      hasNoSelection
    ) {
      window.location.reload();
    }

    setSelectedIds(ids);
  };

  const handleReflectSelection = (e) => {
    e.preventDefault(); // prevents default button behavior
    setIsButtonDisabled(true);
    // getAdsAnalyse({ formattedEnd, formattedStart,selectedIds ,selectedTab });

  };


  const [showAds, setShowAds] = useState(
    localStorage.getItem("show-ads") === "true"
  );

  useEffect(() => {
    setShowAds(statusValues?.showAds);
  }, [statusValues]);

  if (accessDenied) {
    return (
      <div className='d-flex justify-content-center' style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}>
        <AllowedCard />
      </div>
    )
  }

  return (
    <>
      <CommonComponent
        defaultRange={selectedRange}
        onRangeChange={handleDateChange}
        categoryName={categoryName}
        categoryId={categoryId}
        onBreadcrumbClick={onBreadcrumbClick}
        viewType={viewType}
      />
      {!showAds || showAds === "false" ? (
        <DashboardReady />
      ) : (

        <section className="section dashboard py-2">
          <div className="container-fluid">
            <div className="row m-0">
              {/* <PermissionControlledCard permission={permission?.data?.ads_analysis?.kpi?.performance_metrics} colClass='col-12 p-0'> */}
              <div className={(permission?.data?.ads_analysis?.kpi?.performance_metrics?.display === false && permission?.data?.ads_analysis?.kpi?.performance_metrics?.allowed === false) ? "d-none" : "d-block col-12 p-0"}>
              <div className="col-12 kpi-section">
                {/* <SalesCard formattedStart={formattedStart} formattedEnd={formattedEnd} referKey={"ads_analysis"}/> */}
                <AdsSalesCard
                  loadingState={loadingState}
                   categoryPerformanceState={categoryPerformanceState}
                   getCategoryPerformanceDashbaord={getCategoryPerformanceDashbaord}
                    accessDenied={accessDenied} 
                  formattedStart={formattedStart} formattedEnd={formattedEnd} selectedIds={selectedIds} selectedTab={selectedTab} isButtonDisabled={isButtonDisabled} type={"child"} permission={permission?.data?.ads_analysis?.kpi?.performance_metrics} 
                  />


              </div>
              </div>
              {/* </PermissionControlledCard> */}
              
              {/* Business Revenue Pie Chart */}
              {/* <div className="col-12">
              <div className="card">
                <div className="card-body h-auto">
                  <div className="d-flex justify-content-between align-items-baseline">
                    <h5 className="card-title">{t("dashboardHome.bussinessRevenue")}</h5>
                  </div>
                  {loading ? (
                    <Spinner height={300} />
                  ) : (
                  <CategoryPerformanceDashboard chartData={categoryPerformanceState} />
                  )}
                </div>
              </div>
            </div> */}
              <PermissionControlledCard
                permission={permission?.data?.ads_analysis?.table_section?.ads_data}
                colClass="col-12 p-0 my-2"
              >
                <div className="col-12 m-0 kpi-section">
                  <div className="">
                    <div className="card-body h-auto">
                      <div className="justify-content-between align-items-baseline">
                        <div className="d-flex justify-content-between align-items-baseline">
                          <h5 className="kpi-heading">
                            {/* {t("sidebar.regionalWiseSales")} */}
                          </h5>
                          <PermissionControlledCard
                            permission={combinedPermission}
                            colClass="col-0"
                          >
                            <ViewTypeSelect
                              value={viewType}
                              onChange={handleViewTypeChange}
                              permissions={
                                permission?.data?.ads_analysis?.view_format
                              }
                            />
                          </PermissionControlledCard>
                        </div>
                        <Tabs
                          tabs={filteredTabs}
                          onChange={(val) => setSelectedTab(val)}
                        />
                        {adsAnalyseLoaded &&
                          Array.isArray(adsAnalyseState) &&
                          adsAnalyseState.length === 0 ? (
                          <div className='d-flex justify-content-center' style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}>
                            No data found for the given criteria.
                          </div>
                        ) : (
                          (selectedTab && canShowTable) && (
                            <PermissionControlledCard
                              permission={
                                combinedTablePermission
                              }
                              colClass="col-12"
                            >
                              <NormalTable
                                tableId="adsAnalyse"
                                data={adsAnalyseState}
                                selectedIds={selectedIds}
                                onSelectionChange={handleSelectionChange}
                                prioritizeSelected={isButtonDisabled}
                                onCategoryClick={handleCategoryClick}
                                onParentProductClick={handleParentProductClick}
                                loading={loadingState?.adsAnalyseState}
                                sequence={adsAnalyseSequence}
                                downloadBtnPermission={downloadBtnPermission}
                                isProductwiseTable={viewType === 'product'}
                                onColumnChange={handleColumnChange}
                                showSelectColumn={true}
                                section={"ads"}
                              />
                            </PermissionControlledCard>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </PermissionControlledCard>
            </div>
          </div>
        </section>
      )}
      {selectedIds.productId.length + selectedIds.categoryId.length > 0 && (
        <button
          className="floating-btn-select"
          onClick={handleReflectSelection}
          disabled={isButtonDisabled}
        >
          ({selectedIds.productId.length + selectedIds.categoryId.length}){" "}
          {t("checkboxSelect.heading")}
        </button>
      )}
    </>
  );
};

export default AdsAnalysis;
